import { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { authFetch } from '../Utilities/Api';
import { getSocket } from '../socket';
import AuthContext from '../context/AuthContext';
// ✅ Import the new sound utilities
import { playSentSound, playReceivedSound } from '../Utilities/soundEffects';

const PAGE_SIZE = 20;

const normalizeType = (t) => {
    const v = String(t || '').toUpperCase();
    if (v === 'GROUP' || v === 'GROUPS') return 'GROUP';
    return 'DM';
};

const safeSenderId = (msg) =>
    msg?.sender?._id?.toString?.() ??
    msg?.sender?._id ??
    msg?.sender?.toString?.() ??
    msg?.sender;

const useMessageActions = () => {
    const [messagesById, setMessagesById] = useState({});
    const [pageById, setPageById] = useState({});
    const [hasMoreById, setHasMoreById] = useState({});
    const [newMessageById, setNewMessageById] = useState({});

    const { user } = useContext(AuthContext);

    const userIdRef = useRef(user?._id);
    useEffect(() => { userIdRef.current = user?._id; }, [user?._id]);

    const messagesRef = useRef(messagesById);
    useEffect(() => { messagesRef.current = messagesById; }, [messagesById]);

    const inflightLoadsRef = useRef(new Set());
    const lastSeenSentRef = useRef({});

    const socketInstance = getSocket();

    // ---------------------------------------------------------
    // 📡 1. Real-Time Socket Listeners
    // ---------------------------------------------------------
    useEffect(() => {
        const socket = socketInstance;
        if (!socket) return;

        const handleIncoming = (msg) => {
            const chatId = msg.chat?._id || msg.chat;
            if (!chatId) return;

            setMessagesById((prev) => {
                const list = prev[chatId] || [];
                const isDuplicate = list.some(m =>
                    String(m._id) === String(msg._id) ||
                    (msg.tempId && String(m._id) === String(msg.tempId))
                );

                if (isDuplicate) return prev;

                const isFromMe = String(safeSenderId(msg)) === String(userIdRef.current);

                // 🎵 Play sound for incoming messages ONLY
                if (!isFromMe) {
                    playReceivedSound();
                }

                const newMsg = {
                    ...msg,
                    fromSelf: isFromMe
                };

                return { ...prev, [chatId]: [...list, newMsg] };
            });
        };

        const handleSeenUpdate = ({ chatId, userId, seenAt }) => {
            // If I am the one who sent the 'seen' event (on another tab), 
            // we update our local state to reflect the read status.
            setMessagesById((prev) => {
                const list = prev[chatId] || [];
                if (list.length === 0) return prev;

                const updatedList = list.map(m => {
                    const alreadyRead = m.readBy?.some(r =>
                        String(r.user?._id || r.user) === String(userId)
                    );

                    // Mark as read if the message was sent by me and the other user just saw it
                    // OR if I am the one who just saw it.
                    if (!alreadyRead) {
                        return {
                            ...m,
                            readBy: [...(m.readBy || []), { user: userId, readAt: seenAt || new Date() }]
                        };
                    }
                    return m;
                });
                return { ...prev, [chatId]: updatedList };
            });
        };

        socket.on('newMessage', handleIncoming);
        socket.on('messagesSeen', handleSeenUpdate);

        return () => {
            socket.off('newMessage', handleIncoming);
            socket.off('messagesSeen', handleSeenUpdate);
        };
    }, [socketInstance]);

    // ---------------------------------------------
    // 🔍 2. Load messages (GET History)
    // ---------------------------------------------
    const loadMessages = useCallback(
        async (chatId, page = 1, replace = false, currentUserId, chatType = 'DM') => {
            if (!chatId) return;
            const type = normalizeType(chatType);
            const key = `${chatId}:${page}:${type}`;
            if (inflightLoadsRef.current.has(key)) return;
            inflightLoadsRef.current.add(key);

            try {
                const url = `/chats/${chatId}/messages?page=${page}&limit=${PAGE_SIZE}`;
                const res = await authFetch(url);
                const data = await res.json();
                const messageList = data.messages || data;

                if (!res.ok || !Array.isArray(messageList)) {
                    setHasMoreById((p) => ({ ...p, [chatId]: false }));
                    return;
                }

                const transformed = messageList.map((msg) => ({
                    ...msg,
                    fromSelf: String(safeSenderId(msg)) === String(currentUserId)
                }));

                setMessagesById((prev) => {
                    if (replace) return { ...prev, [chatId]: transformed };
                    const existing = prev[chatId] || [];
                    const seenSet = new Set(existing.map((m) => String(m._id)));
                    const merged = [...existing];

                    for (const msg of transformed) {
                        if (!seenSet.has(String(msg._id))) merged.unshift(msg);
                    }
                    return { ...prev, [chatId]: merged };
                });

                setPageById((p) => ({ ...p, [chatId]: page }));
                setHasMoreById((p) => ({ ...p, [chatId]: messageList.length === PAGE_SIZE }));
            } catch (err) {
                console.error('❌ Failed to load messages:', err);
            } finally {
                inflightLoadsRef.current.delete(key);
            }
        },
        []
    );

    // ---------------------------------------------
    // ✉️ 3. Send message (POST)
    // ---------------------------------------------
    const handleSendMessage = useCallback(
        async ({ chatId, content, replyTo = null }) => {
            const currentUid = userIdRef.current;

            if (!chatId || !currentUid || !content?.trim()) return;

            const trimmedContent = content.trim();
            const tempMessageId = uuidv4();

            const placeholder = {
                _id: tempMessageId,
                tempId: tempMessageId,
                content: trimmedContent,
                sender: { _id: String(currentUid), username: user?.username },
                fromSelf: true,
                createdAt: new Date().toISOString(),
                status: 'sending',
                readBy: [{ user: currentUid, readAt: new Date() }],
                replyTo: replyTo,
                reactions: [],
            };

            setMessagesById((prev) => ({
                ...prev,
                [chatId]: [...(prev[chatId] || []), placeholder],
            }));

            try {
                const replyToId = replyTo?._id || replyTo;

                const res = await authFetch(`/chats/${chatId}/messages`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        content: trimmedContent,
                        replyTo: replyToId || null,
                        tempId: tempMessageId
                    }),
                });

                if (!res.ok) throw new Error(`Server Error: ${res.status}`);

                const saved = await res.json();

                // 🎵 Play "Sent" sound immediately upon successful API response
                playSentSound();

                setMessagesById((prev) => {
                    const list = prev[chatId] || [];
                    return {
                        ...prev,
                        [chatId]: list.map(m =>
                            String(m._id) === String(tempMessageId)
                                ? { ...saved, fromSelf: true, status: 'sent' }
                                : m
                        )
                    };
                });

                socketInstance?.emit('stopTyping', { chatId, userId: currentUid });
            } catch (err) {
                console.error('❌ Send failed:', err);
                setMessagesById((prev) => ({
                    ...prev,
                    [chatId]: (prev[chatId] || []).map(m =>
                        String(m._id) === String(tempMessageId) ? { ...m, status: 'failed' } : m
                    )
                }));
            }
        },
        [socketInstance, user?.username]
    );

    // ---------------------------------------------
    // 👀 4. Mark Messages Seen (Socket + API)
    // ---------------------------------------------
    const markMessagesSeen = useCallback(async (chatId, currentUserId) => {
        if (!chatId || !currentUserId) return;

        const list = messagesRef.current?.[chatId] || [];

        // Check for messages I haven't read yet
        const unread = list.filter(m => {
            const isFromOther = String(safeSenderId(m)) !== String(currentUserId);
            const iHaveNotRead = !m.readBy?.some(r => String(r.user?._id || r.user) === String(currentUserId));
            return isFromOther && iHaveNotRead;
        });

        if (!unread.length) return;

        const lastMsgId = String(unread[unread.length - 1]._id);
        if (lastSeenSentRef.current[chatId] === lastMsgId) return;

        try {
            lastSeenSentRef.current[chatId] = lastMsgId;

            // ✅ Emit socket first for instant UI feedback on the sender's side
            socketInstance?.emit('messagesSeen', { chatId, userId: currentUserId });

            // ✅ Backup: Tell the API to persist the read status
            await authFetch(`/chats/${chatId}/seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chatId })
            });

            // Update local state to clear unread counts immediately
            setMessagesById((prev) => ({
                ...prev,
                [chatId]: (prev[chatId] || []).map(m => {
                    const isFromOther = String(safeSenderId(m)) !== String(currentUserId);
                    if (isFromOther) {
                        const alreadyRead = m.readBy?.some(r => String(r.user?._id || r.user) === String(currentUserId));
                        if (!alreadyRead) {
                            return {
                                ...m,
                                readBy: [...(m.readBy || []), { user: currentUserId, readAt: new Date() }]
                            };
                        }
                    }
                    return m;
                })
            }));
        } catch (err) {
            console.error('❌ Failed to mark seen:', err);
            delete lastSeenSentRef.current[chatId];
        }
    }, [socketInstance]);

    // ---------------------------------------------
    // 🎭 5. Reactions
    // ---------------------------------------------
    const handleReactToMessage = useCallback(async (chatId, messageId, emoji) => {
        const currentUid = userIdRef.current;
        if (!currentUid || !chatId || !messageId || !emoji) return;
        try {
            const res = await authFetch(`/chats/${chatId}/messages/${messageId}/react`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ emoji }),
            });
            const updated = await res.json();
            setMessagesById((prev) => {
                const list = prev[chatId] || [];
                return {
                    ...prev,
                    [chatId]: list.map(m =>
                        String(m._id) === String(messageId)
                            ? { ...m, reactions: updated.reactions || [] }
                            : m
                    )
                };
            });
        } catch (err) {
            console.error('❌ Failed to react:', err);
        }
    }, []);

    return {
        messagesById,
        pageById,
        hasMoreById,
        newMessageById,
        setNewMessageById,
        loadMessages,
        handleSendMessage,
        markMessagesSeen,
        setMessagesById,
        handleReactToMessage,
    };
};

export default useMessageActions;